import {
  ErrorResult,
  PollOutcomeFn,
  StartTransactionFn,
  Transaction,
} from "./types";
import { MUTATION_START_TRANSACTION, QUERY_POLL_OUTCOME } from "./queries";
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { TOWER_PAYMENTS_GRAPHQL_ENDPOINT } from "./constants";
import { v4 as uuid4 } from "uuid";

export interface TowerPaymentsSDK {
  startTransaction: StartTransactionFn;
  pollOutcome: PollOutcomeFn;
}

interface InitOptions {
  token: string;
  endpoint?: string;
}

export function createTowerPaymentsSDK({
  token,
  endpoint = TOWER_PAYMENTS_GRAPHQL_ENDPOINT,
}: InitOptions): TowerPaymentsSDK {
  if (!token) {
    throw new Error(
      "A secret is required to initialize the Tower Payments SDK."
    );
  }

  const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    link: new HttpLink({
      uri: endpoint,
      headers: {
        ["x-device-token"]: `Bearer ${token}`,
      },
    }),
    cache: new InMemoryCache(),
  });

  const startTransaction: StartTransactionFn = async (
    amountInCents,
    transactionType
  ) => {
    const response = await client.mutate<{
      createTransaction: Transaction | ErrorResult;
    }>({
      mutation: MUTATION_START_TRANSACTION,
      variables: {
        input: {
          amountInCents,
          transactionType,
          transactionRef: uuid4(), // dont careabout transactionRef
        },
      },
    });

    const data = response.data?.createTransaction;

    if (!data) {
      throw new Error("Failed to start transaction.");
    }

    return data;
  };

  const pollOutcome: PollOutcomeFn = async (transactionId: string) => {
    const response = await client.query<{
      transaction: Transaction;
    }>({
      query: QUERY_POLL_OUTCOME,
      variables: { id: transactionId },
      fetchPolicy: "network-only",
    });

    const data = response.data?.transaction;
    return data;
  };

  return {
    startTransaction,
    pollOutcome,
  };
}
